/* Mobile menu drawer controller -- ported from orion-v3.0/src/js/
 * components/mobilemenu.js, simplified for the H3.0 architecture.
 *
 * Single drawer per site (sections/mobile-menu.liquid restricts the
 * section to the header group, intended as a singleton).
 *
 * Open / close:
 *   - Click [data-trigger="mobile-menu"] (the navbar hamburger /
 *     close icon) toggles the drawer.
 *   - Click anywhere inside `.mobile-menu` but outside
 *     `.mobile-menu__container` (the overlay strip below the
 *     drawer in Auto / Fixed height modes) closes.
 *   - ESC closes.
 *   - On close the controller resets view state to the drawer's
 *     "first render" state: in Views the default content
 *     is visible (no view active), in Tabs the first trigger and
 *     its view are active.
 *
 * View switching:
 *   - The same set of trigger elements (.mobile-menu-view-trigger-js
 *     emitted by the section's auto-rendered default nav) drives
 *     both modes. The handler branches on the drawer's display
 *     mode:
 *       Views: activate the matching .mobile-menu__view
 *         and push to a back-stack. The back button inside each
 *         view pops one entry off the stack -- supports nested
 *         triggers (the Link List block can carry view-trigger
 *         buttons too) where a view's content links to another
 *         view.
 *       Tabs: swap which trigger is `.is-active` AND which view
 *         is `.is-active`. The trigger's active class drives the
 *         tab-style bottom-border highlight on the strip.
 *
 * Body class:
 *   navbar--show-mobile-menu  added while the drawer is open. CSS
 *                              uses it to lock page scroll and to
 *                              swap the hamburger icon to an X. */

class MobileMenuController {
  constructor() {
    this.drawer = null;
    /** Views back-stack: top of stack is the currently
     *  visible view; empty stack = default content shown. Only
     *  used in Views mode. */
    this.viewStack = [];
    /** Timer used to defer the view-state reset until after the
     *  drawer's close fade completes, so the merchant doesn't see
     *  tabs / views snap back mid-transition. Cleared on reopen. */
    this.closeResetTimer = null;
  }

  init() {
    this.drawer = document.querySelector('[data-mobile-menu]');
    if (!this.drawer) return;

    this.bindOpenTriggers();
    this.bindDrawerCloseSurfaces();
    this.bindEscape();
    this.bindViewTriggers();
    this.bindBackButtons();

    // Tabs mode boots with the first trigger + its view active.
    // The Liquid template intentionally doesn't pre-render this
    // state so there's a single source of truth for tabs init.
    this.initTabsMode();
  }

  bindOpenTriggers() {
    document.querySelectorAll('[data-trigger="mobile-menu"]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });
  }

  bindDrawerCloseSurfaces() {
    this.drawer.addEventListener('click', (e) => {
      // Bottom bar sits outside .mobile-menu__container; treat it as
      // inside the drawer so opening the currency <select> doesn't
      // count as an overlay click and close the menu.
      if (!e.target.closest('.mobile-menu__container, .mobile-menu__bottom-bar')) {
        this.close();
      }
    });
  }

  bindEscape() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  bindViewTriggers() {
    this.drawer.querySelectorAll('.mobile-menu-view-trigger-js').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = trigger.dataset.mobileViewTrigger;
        if (!handle) return;

        if (this.isTabsMode()) {
          this.activateTab(handle, trigger);
        } else {
          this.openView(handle);
        }
      });
    });
  }

  bindBackButtons() {
    this.drawer.querySelectorAll('.mobile-menu-back-js').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.goBack();
      });
    });
  }

  // The Link List block uses native <details> + <summary> for the
  // accordion toggle (see blocks/_mobile-menu-linklist.liquid +
  // component-mobile-menu.css). No click binding needed -- the
  // browser handles open/close on summary click. We only need a
  // bulk-collapse call during the close-reset so the next open
  // boots with every accordion closed.
  collapseAllAccordions() {
    this.drawer.querySelectorAll('.mobile-menu-linklist__details[open]').forEach((d) => {
      d.removeAttribute('open');
    });
  }

  isOpen() {
    return document.body.classList.contains('navbar--show-mobile-menu');
  }

  isTabsMode() {
    return this.drawer.dataset.displayMode === 'tabs';
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // A pending reset from a previous close hasn't fired yet --
    // cancel it; the drawer is opening again and we don't want
    // to mid-state-stomp once it's visible.
    if (this.closeResetTimer) {
      clearTimeout(this.closeResetTimer);
      this.closeResetTimer = null;
    }
    document.body.classList.add('navbar--show-mobile-menu');
    this.drawer.classList.add('is-active');
    this.updateTriggerAria(true);
  }

  close() {
    // Return focus to the hamburger trigger before the drawer
    // disappears, but only if focus was inside the drawer when
    // close fired. Outside-click closes shouldn't yank focus from
    // wherever the user is now.
    const drawerHadFocus = this.drawer.contains(document.activeElement);

    document.body.classList.remove('navbar--show-mobile-menu');
    this.drawer.classList.remove('is-active');
    this.updateTriggerAria(false);

    if (drawerHadFocus) {
      const trigger = document.querySelector('[data-trigger="mobile-menu"]');
      if (trigger) trigger.focus();
    }

    // Defer the view-state reset until the close fade finishes,
    // so the merchant doesn't see tabs swap or views deactivate
    // while the drawer is still visible. Cancelled on reopen.
    if (this.closeResetTimer) clearTimeout(this.closeResetTimer);
    this.closeResetTimer = window.setTimeout(() => {
      this.closeResetTimer = null;
      // Drawer reopened during the fade -- bail, don't stomp.
      if (this.isOpen()) return;
      this.resetState();
    }, this.getCloseTransitionMs());
  }

  /** Read the drawer's actual transition duration (set via
   *  --global-transition-duration) so the reset delay tracks
   *  theme settings rather than a hard-coded number. */
  getCloseTransitionMs() {
    const dur = getComputedStyle(this.drawer).transitionDuration.split(',')[0].trim();
    if (dur.endsWith('ms')) return parseFloat(dur) || 300;
    if (dur.endsWith('s')) return (parseFloat(dur) || 0.3) * 1000;
    return 300;
  }

  // Views path: activate the named view and push onto the
  // back-stack.
  openView(handle) {
    const view = this.drawer.querySelector(`.mobile-menu__view[data-view="${handle}"]`);
    if (!view) return;
    this.deactivateAllViews();
    view.classList.add('is-active');
    // Re-clicking the same trigger shouldn't grow the stack.
    if (this.viewStack[this.viewStack.length - 1] !== handle) {
      this.viewStack.push(handle);
    }
  }

  goBack() {
    this.viewStack.pop();
    this.deactivateAllViews();
    const previous = this.viewStack[this.viewStack.length - 1];
    if (previous) {
      const view = this.drawer.querySelector(`.mobile-menu__view[data-view="${previous}"]`);
      if (view) view.classList.add('is-active');
    }
  }

  // Tabs path: swap which trigger + view is .is-active. No stack
  // (tabs don't nest in the same way views do; clicking a tab
  // jumps directly to that view).
  activateTab(handle, trigger) {
    this.drawer.querySelectorAll('.mobile-menu-view-trigger-js').forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    trigger.classList.add('is-active');
    trigger.setAttribute('aria-selected', 'true');

    this.deactivateAllViews();
    const view = this.drawer.querySelector(`.mobile-menu__view[data-view="${handle}"]`);
    if (view) view.classList.add('is-active');
  }

  initTabsMode() {
    if (!this.isTabsMode()) return;
    const firstTrigger = this.drawer.querySelector('.mobile-menu-view-trigger-js');
    if (!firstTrigger) return;
    const handle = firstTrigger.dataset.mobileViewTrigger;
    if (!handle) return;
    this.activateTab(handle, firstTrigger);
  }

  // Called on close. Returns the drawer to its "first open" state
  // so the next open doesn't inherit the previous session's view
  // or any expanded link-list accordions.
  resetState() {
    this.deactivateAllViews();
    this.collapseAllAccordions();
    if (this.isTabsMode()) {
      this.initTabsMode();
    } else {
      this.drawer.querySelectorAll('.mobile-menu-view-trigger-js').forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      this.viewStack = [];
    }
  }

  deactivateAllViews() {
    this.drawer.querySelectorAll('.mobile-menu__view').forEach((v) => {
      v.classList.remove('is-active');
    });
  }

  updateTriggerAria(open) {
    document.querySelectorAll('[data-trigger="mobile-menu"]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }
}

function start() {
  new MobileMenuController().init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
